<template>
  <div class="modal-card card">
    <div class="card-header">
      <h4 class="card-header-title">Execution Details</h4>
      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
    </div>
    <div class="card-body">
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-2 text-muted">Loading execution details...</p>
      </div>

      <div v-else>
        <!-- Failure Reason Alert -->
        <div v-if="execution?.status.toLowerCase() == 'failed'" class="alert alert-danger mb-4 error-alert" role="alert">
          <div class="d-flex align-items-start">
            <i class="fa fa-exclamation-triangle me-3 mt-1 fs-4"></i>
            <div class="flex-grow-1">
              <p class="mb-0">{{ execution?.reason || 'Execution failed with no error message available.' }}</p>
            </div>
          </div>
        </div>

        <!-- Execution Summary -->
        <div class="row mb-4">
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Execution ID</label>
              <p class="mb-0 fw-bold font-monospace">{{ execution?.id || execution?.execution_id || '-' }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Status</label>
              <p class="mb-0">
                <span :class="getStatusBadgeClass(execution?.status)">
                  {{ execution?.status || '-' }}
                </span>
              </p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Submission Time</label>
              <p class="mb-0">{{ formatDateTime(execution?.statistics?.submission_time) }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Completion Time</label>
              <p class="mb-0">{{ formatDateTime(execution?.statistics?.completion_time) }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Duration</label>
              <p class="mb-0">{{ calculateDuration() }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold text-muted small">Data Scanned</label>
              <p class="mb-0">{{ formatBytes(execution?.statistics?.data_scanned_bytes || 0) }}</p>
            </div>
          </div>
        </div>

        <!-- Results Section -->
        <div v-if="hasResultPayload" class="border-top pt-4">
          <h4 class="mb-3 pb-1">
            <i class="fa-solid fa-chart-simple me-2"></i>
            Results
          </h4>

          <!-- Simple Count Result -->
          <div v-if="isSimpleCountResult" class="total-count-card">
            <div class="card bg-primary text-white">
              <div class="card-body text-center py-4">
                <h6 class="text-uppercase text-white-50">
                  Total Audience Size
                </h6>
                <h1 class="display-4 fw-bold mb-0">{{ formatNumber(getSimpleCount) }}</h1>
              </div>
            </div>
          </div>

          <!-- Count Result (legacy format) -->
          <div v-else-if="execution?.query_type === 'count'" class="card bg-light">
            <div class="card-body text-center">
              <div class="text-muted small">Audience Count</div>
              <div class="fs-1 fw-bold text-primary">{{ formatNumber(getResultCount()) }}</div>
            </div>
          </div>

          <!-- Histogram Result with Charts -->
          <div v-else-if="hasHistogramData" class="histogram-charts">
            <!-- Total Count Card for Histogram Results -->
            <div v-if="totalCount > 0" class="total-count-card mb-4">
              <div class="card bg-primary text-white">
                <div class="card-body text-center py-4">
                  <h6 class="text-uppercase text-white-50">
                    Total Audience Size
                  </h6>
                  <h1 class="display-4 fw-bold mb-0">{{ formatNumber(totalCount) }}</h1>
                </div>
              </div>
            </div>

            <AttributeCharts
              v-if="sortedAttributes.length > 0"
              :attributes="sortedAttributes"
              :record-total="totalCount"
            />
            <div v-else class="text-center py-4 text-muted">
              <i class="fa fa-chart-pie fa-3x mb-3"></i>
              <p>No attribute data available to display</p>
            </div>
          </div>
          <div v-else-if="resultRows.length > 0">
            <Datatable
              :key="resultTableKey"
              tableId="executionResultRows"
              :columns="resultTableColumns"
              :data="resultTableData"
              :ignoreCache="true"
              :showLoading="loading"
              :select="false"
              :enableCSVExport="true"
              :compact="true"
              :info="true"
              @exportCSV="exportResultCSV"
            />
          </div>

          <div v-else-if="resultData" class="text-center py-4 text-muted">
            <i class="fa fa-table fa-3x mb-3"></i>
            <p class="mb-0">This execution returned no rows.</p>
          </div>

          <!-- Generic Result -->
          <div v-else>
            <pre class="bg-dark text-white p-3 rounded"
              style="max-height: 300px; overflow: auto; font-size: 0.875rem;">{{ formatResult() }}</pre>
          </div>
        </div>

        <!-- Query Details -->
        <div v-if="execution?.query || execution?.criterion" class="border-top pt-3 mt-3">
          <h5 class="mb-3">
            <i class="fa fa-code me-2"></i>
            Query Details
          </h5>
          <pre class="bg-dark text-white p-3 rounded"
            style="max-height: 200px; overflow: auto; font-size: 0.875rem;">{{ formatQuery() }}</pre>
        </div>

        <!-- Error Message -->
        <div v-if="execution?.error || execution?.error_message" class="alert alert-danger mt-3 error-alert">
          <strong>Error:</strong> {{ execution?.error || execution?.error_message }}
        </div>

        <div v-if="error" class="alert alert-danger mt-3 error-alert">
          {{ error }}
        </div>
      </div>
    </div>
    <div class="card-footer text-start">
      <button type="button" class="btn btn-secondary" @click="refreshExecution" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2" />
        <i v-else class="fa fa-refresh me-1"></i>
        Refresh
      </button>
      <button v-if="hasDownloadableResult" type="button" class="btn btn-outline-primary ms-2" @click="downloadResult">
        <i class="fa fa-download me-1"></i>
        Download Result
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Datatable from '../Datatable.vue'
import csv from '../../utils/tools/csv'
import { DateTimeFormat, formatBytes } from '../../utils/commonFunction'
import AttributeCharts from '../audience/AttributeCharts.vue'

export default {
  name: 'ExecutionDetail',
  components: {
    AttributeCharts,
    Datatable,
  },
  props: {
    modalData: Object,
  },
  data() {
    return {
      loading: false,
      error: null,
      executionResult: null,
      singleAttributes: {},
      totalCount: 0,
    }
  },
  computed: {
    execution() {
      return this.executionResult || this.modalData?.execution
    },
    dataEnvironmentId() {
      return this.modalData?.dataEnvironmentId
    },
    dataset() {
      return this.modalData?.dataset
    },
    // Single source of truth for the audsum result payload: { header: [...], body: [[...]] }.
    // The execution detail serves it at .data; legacy shapes nest it under .result/.response.
    resultData() {
      return this.execution?.data || this.execution?.result?.data || this.execution?.response?.data
    },
    resultHeader() {
      return Array.isArray(this.resultData?.header) ? this.resultData.header : []
    },
    resultRows() {
      return Array.isArray(this.resultData?.body) ? this.resultData.body : []
    },
    // Datatable reads its columns once at creation, so it is only mounted (and
    // remounted, via this key) once a header for the current result is known.
    resultTableKey() {
      return this.resultColumnKeys.join('|')
    },
    // DataTables treats a dot in `data` as object notation, so keys are flattened.
    resultColumnKeys() {
      if (this.resultHeader.length > 0) {
        return this.resultHeader.map(name => String(name).replace(/\./g, '_'))
      }
      const widest = this.resultRows.reduce((max, row) => Math.max(max, row?.length || 0), 0)
      return Array.from({ length: widest }, (unused, i) => `column_${i + 1}`)
    },
    resultTableColumns() {
      return this.resultColumnKeys.map((key, i) => ({
        data: key,
        title: this.formatAttributeName(String(this.resultHeader[i] ?? key)),
      }))
    },
    resultTableData() {
      const keys = this.resultColumnKeys
      return this.resultRows.map(row => {
        const record = {}
        keys.forEach((key, i) => {
          record[key] = Array.isArray(row) ? row[i] : row?.[key]
        })
        return record
      })
    },
    // Render the Results section for any payload, including one with zero rows.
    hasResultPayload() {
      return Boolean(this.execution?.result || this.execution?.response || this.resultData)
    },
    // 'completed' is a success status here too - the history table treats it as one.
    isSuccessStatus() {
      const status = (this.execution?.status || '').toLowerCase()
      return status === 'success' || status === 'succeeded' || status === 'completed'
    },
    hasDownloadableResult() {
      return Boolean(this.execution?.result || this.execution?.response || this.resultData)
    },
    hasHistogramData() {
      const data = this.resultData
      if (!data || !data.header || !data.body || !Array.isArray(data.body) || data.body.length === 0) {
        return false
      }
      // Exclude count results (header contains "count" but not "attribute_name")
      if (data.header.includes('count') && !data.header.includes('attribute_name')) {
        return false
      }
      // Check if it has the histogram structure (attribute_name, attribute_value, count)
      return data.header.includes('attribute_name') && data.header.includes('attribute_value')
    },
    isSimpleCountResult() {
      const data = this.resultData
      if (!data || !data.header || !data.body || !Array.isArray(data.body) || data.body.length === 0) {
        return false
      }
      // Check if it's a count result (header contains "count")
      // This includes cases like ["count"] or ["count", "scaled_count"]
      return data.header.includes('count') && !data.header.includes('attribute_name')
    },
    getSimpleCount() {
      const data = this.resultData
      if (!this.isSimpleCountResult || !data?.body || !Array.isArray(data.body) || data.body.length === 0) {
        return null
      }
      const countIndex = data.header.indexOf('count')
      const firstRow = data.body[0]
      if (firstRow && countIndex !== -1 && firstRow[countIndex] !== undefined) {
        return parseInt(firstRow[countIndex], 10) || 0
      }
      // Fallback: if count is the first column, use index 0
      if (firstRow && firstRow.length > 0) {
        return parseInt(firstRow[0], 10) || 0
      }
      return null
    },
    sortedAttributes() {
      return Object.entries(this.singleAttributes)
        .map(([name, values]) => ({ name, values }))
        .filter(attr => attr.name !== '-') // Exclude the total count marker
        .sort((a, b) => {
          // Sort by number of values (ascending) for better layout
          return a.values.length - b.values.length
        })
    },
  },
  methods: {
    ...mapActions({
      fetchExecutionResult: 'audience/fetchExecutionResult',
      addToast: 'addToast',
    }),
    formatBytes,
    async refreshExecution() {
      if (!this.dataEnvironmentId || !this.dataset || !(this.execution?.id || this.execution?.execution_id)) return

      this.loading = true
      this.error = null

      try {
        this.executionResult = await this.fetchExecutionResult({
          dataEnvironmentId: this.dataEnvironmentId,
          dataset: this.dataset,
          executionId: this.execution?.id || this.execution?.execution_id,
        })
        // Parse histogram data if available
        this.$nextTick(() => {
          this.parseHistogramData()
        })
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to refresh execution'
      } finally {
        this.loading = false
      }
    },
    formatDateTime(dateString) {
      return DateTimeFormat(dateString)
    },
    formatNumber(num) {
      if (num == null) return '-'
      if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B'
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
      if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
      return num.toLocaleString()
    },
    calculateDuration() {
      const durationMs = this.execution?.statistics?.execution_time_millis || 0

      if (durationMs < 1000) return `${durationMs} ms`
      if (durationMs < 60000) return `${(durationMs / 1000).toFixed(2)} s`
      if (durationMs < 3600000) return `${(durationMs / 60000).toFixed(2)} m`
      return `${(durationMs / 3600000).toFixed(2)} h`
    },
    getStatusBadgeClass(status) {
      const statusLower = (status || '').toLowerCase()
      if (statusLower === 'success' || statusLower === 'succeeded') {
        return 'badge bg-success'
      } else if (statusLower === 'failed' || statusLower === 'error') {
        return 'badge bg-danger'
      } else if (statusLower === 'running' || statusLower === 'in_progress') {
        return 'badge bg-warning'
      } else if (statusLower === 'pending' || statusLower === 'queued') {
        return 'badge bg-info'
      }
      return 'badge bg-secondary'
    },
    getResultCount() {
      const result = this.execution?.result || this.execution?.response
      if (!result) return null
      if (typeof result === 'number') return result
      if (Array.isArray(result) && result[0]?.count) return result[0].count
      if (result.count) return result.count
      return null
    },
    getHistogramData() {
      const result = this.execution?.result || this.execution?.response
      if (!result) return []
      if (Array.isArray(result)) return result
      if (result.histogram) return result.histogram
      if (result.data) return result.data
      return []
    },
    parseHistogramData() {
      const data = this.resultData
      if (!data?.body || !Array.isArray(data.body)) {
        this.singleAttributes = {}
        this.totalCount = 0
        return
      }

      const body = data.body
      const header = data.header || []
      const nameIndex = header.indexOf('attribute_name')
      const valueIndex = header.indexOf('attribute_value')
      const countIndex = header.indexOf('count')

      // First row is always total count (attribute_name: "-", attribute_value: "-")
      if (body.length > 0) {
        const firstRow = body[0]
        const firstAttrName = firstRow[nameIndex !== -1 ? nameIndex : 0]
        const firstAttrValue = firstRow[valueIndex !== -1 ? valueIndex : 1]
        if (firstAttrName === '-' && firstAttrValue === '-') {
          this.totalCount = parseInt(firstRow[countIndex !== -1 ? countIndex : 2], 10) || 0
        } else {
          this.totalCount = 0
        }
      } else {
        this.totalCount = 0
      }

      // Group by attribute name
      const singleAttrs = {}

      body.forEach((row) => {
        const attrName = row[nameIndex !== -1 ? nameIndex : 0]
        const attrValue = row[valueIndex !== -1 ? valueIndex : 1]
        const count = parseInt(row[countIndex !== -1 ? countIndex : 2], 10) || 0

        // Skip the total count row
        if (attrName === '-' && attrValue === '-') return

        // Skip combination attributes (those with {)(} separator)
        if (attrName.includes('{)(}')) return

        if (!singleAttrs[attrName]) singleAttrs[attrName] = []
        singleAttrs[attrName].push({ value: attrValue, count })
      })

      // Sort values: ordinal attributes by leading number, others by count descending
      const ordinalAttributes = ['age_range', 'income_range', 'children', 'education']
      const extractLeadingNumber = (str) => {
        const match = String(str).match(/[\d.]+/)
        return match ? parseFloat(match[0]) : Infinity
      }
      Object.keys(singleAttrs).forEach(key => {
        if (ordinalAttributes.includes(key)) {
          singleAttrs[key].sort((a, b) => extractLeadingNumber(a.value) - extractLeadingNumber(b.value))
        } else {
          singleAttrs[key].sort((a, b) => b.count - a.count)
        }
      })

      this.singleAttributes = singleAttrs
    },
    formatAttributeName(name) {
      // Convert snake_case to Title Case
      return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatResult() {
      const result = this.execution?.result || this.execution?.response || this.resultData
      if (!result) return 'No result data'
      return JSON.stringify(result, null, 2)
    },
    formatQuery() {
      const query = this.execution?.query || this.execution?.criterion
      if (!query) return 'No query data'
      if (typeof query === 'string') {
        try {
          return JSON.stringify(JSON.parse(query), null, 2)
        } catch {
          return query
        }
      }
      return JSON.stringify(query, null, 2)
    },
    exportResultCSV(rows) {
      if (!rows || rows.length === 0) {
        this.addToast({ type: 'error', message: 'No rows to export' })
        return
      }
      csv.exportCSV(`execution_${this.execution?.id || this.execution?.execution_id || 'result'}`, rows)
    },
    downloadResult() {
      const result = this.execution?.result || this.execution?.response || this.resultData
      if (!result) return

      const content = JSON.stringify(result, null, 2)
      const blob = new Blob([content], { type: 'application/json;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `execution_${this.execution?.id || 'result'}_${new Date().toISOString().split('T')[0]}.json`
      link.click()
      URL.revokeObjectURL(link.href)

      this.addToast({ type: 'success', message: 'Result downloaded successfully' })
    },
  },
  mounted() {
    if (this.isSuccessStatus) {
      this.refreshExecution()
    } else {
      // Parse data if already available
      this.$nextTick(() => {
        this.parseHistogramData()
      })
    }
  },
  watch: {
    execution() {
      // Re-parse when execution data changes
      this.$nextTick(() => {
        this.parseHistogramData()
      })
    },
  },
}
</script>

<style scoped>
.form-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.error-alert {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.error-alert p,
.error-alert strong {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
}

.histogram-charts {
  margin-top: 1rem;
}

.total-count-card {
  max-width: 100%;
  box-sizing: border-box;
}

.total-count-card .card {
  background: linear-gradient(135deg, #2C7BE5 0%, #1a5cbd 100%);
  box-shadow: 0 4px 20px rgba(44, 123, 229, 0.3);
  max-width: 100%;
  box-sizing: border-box;
}

</style>
